import type { CardId } from '../engine/types';
import type { MatchDiagnosticBundle } from './analyzer';
import type { DiagnosticLevel, DiagnosticPhase, DiagnosticRecord } from './journal';

type EncodedValue = string | number | boolean | null | EncodedValue[];

interface CompactDictionaries {
  phases: string[];
  sources: string[];
  levels: string[];
  codes: string[];
  cards: string[];
  correlations: string[];
  payloadKeys: string[];
}

export interface CompactDiagnosticBundle {
  formatVersion: 2;
  manifest: MatchDiagnosticBundle['manifest'];
  replay: MatchDiagnosticBundle['replay'];
  checkpoints: MatchDiagnosticBundle['checkpoints'];
  metrics: MatchDiagnosticBundle['metrics'];
  findings: MatchDiagnosticBundle['findings'];
  summary: MatchDiagnosticBundle['summary'];
  diagnostics: {
    codec: 'tuple-delta-1';
    dictionaries: CompactDictionaries;
    records: EncodedValue[][];
  };
}

const dictionaryIndex = (values: string[]) => {
  const indices = new Map<string, number>();
  return (value: string): number => {
    const existing = indices.get(value);
    if (existing !== undefined) return existing;
    const index = values.length;
    values.push(value);
    indices.set(value, index);
    return index;
  };
};

export function compactDiagnosticBundle(bundle: MatchDiagnosticBundle): CompactDiagnosticBundle {
  const dictionaries: CompactDictionaries = { phases: [], sources: [], levels: [], codes: [], cards: [], correlations: [], payloadKeys: [] };
  const phaseIndex = dictionaryIndex(dictionaries.phases);
  const sourceIndex = dictionaryIndex(dictionaries.sources);
  const levelIndex = dictionaryIndex(dictionaries.levels);
  const codeIndex = dictionaryIndex(dictionaries.codes);
  const cardIndex = dictionaryIndex(dictionaries.cards);
  const correlationIndex = dictionaryIndex(dictionaries.correlations);
  const payloadKeyIndex = dictionaryIndex(dictionaries.payloadKeys);

  const encode = (value: unknown): EncodedValue => {
    if (value === null || typeof value === 'string' || typeof value === 'boolean') return value;
    if (typeof value === 'number') {
      if (Number.isFinite(value)) return value;
      return ['n', Number.isNaN(value) ? 'NaN' : value > 0 ? '+Infinity' : '-Infinity'];
    }
    if (value === undefined) return ['u'];
    if (Array.isArray(value)) return ['a', ...value.map(encode)];
    if (typeof value === 'object') {
      const encoded: EncodedValue[] = ['o'];
      for (const [key, child] of Object.entries(value as Record<string, unknown>)) encoded.push(payloadKeyIndex(key), encode(child));
      return encoded;
    }
    return ['u'];
  };

  let previousEventId = 0;
  let previousTick = 0;
  const records = bundle.records.map((record) => {
    const tuple: EncodedValue[] = [
      record.eventId - previousEventId,
      record.tick - previousTick,
      phaseIndex(record.phase),
      sourceIndex(record.source),
      levelIndex(record.level),
      codeIndex(record.code),
      record.commandSequence ?? null,
      record.entityIds ? ['a', ...record.entityIds] : null,
      record.cardIds ? ['a', ...record.cardIds.map((card) => cardIndex(card))] : null,
      record.correlationId === undefined ? null : correlationIndex(record.correlationId),
      encode(record.payload),
    ];
    previousEventId = record.eventId;
    previousTick = record.tick;
    return tuple;
  });

  return {
    formatVersion: 2,
    manifest: bundle.manifest,
    replay: bundle.replay,
    checkpoints: bundle.checkpoints,
    metrics: bundle.metrics,
    findings: bundle.findings,
    summary: bundle.summary,
    diagnostics: { codec: 'tuple-delta-1', dictionaries, records },
  };
}

export function isCompactDiagnosticBundle(value: unknown): value is CompactDiagnosticBundle {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<CompactDiagnosticBundle>;
  return candidate.formatVersion === 2 && candidate.diagnostics?.codec === 'tuple-delta-1';
}

export function expandDiagnosticBundle(compact: CompactDiagnosticBundle): MatchDiagnosticBundle {
  const { dictionaries } = compact.diagnostics;
  const decode = (value: EncodedValue): unknown => {
    if (!Array.isArray(value)) return value;
    if (value[0] === 'u') return undefined;
    if (value[0] === 'n') return value[1] === 'NaN' ? Number.NaN : value[1] === '+Infinity' ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
    if (value[0] === 'a') return value.slice(1).map(decode);
    if (value[0] === 'o') {
      const result: Record<string, unknown> = {};
      for (let index = 1; index < value.length; index += 2) result[dictionaries.payloadKeys[Number(value[index])]!] = decode(value[index + 1]!);
      return result;
    }
    throw new Error(`Unsupported compact value tag: ${String(value[0])}`);
  };
  const decodeNumberArray = (value: EncodedValue): number[] | undefined => Array.isArray(value) && value[0] === 'a' ? value.slice(1).map(Number) : undefined;
  let eventId = 0;
  let tick = 0;
  const records: DiagnosticRecord[] = compact.diagnostics.records.map((tuple) => {
    eventId += Number(tuple[0]);
    tick += Number(tuple[1]);
    const entityIds = tuple[7] === null ? undefined : decodeNumberArray(tuple[7]!);
    const cardIndices = tuple[8] === null ? undefined : decodeNumberArray(tuple[8]!);
    return {
      schemaVersion: 1,
      matchId: compact.manifest.matchId,
      eventId,
      tick,
      phase: dictionaries.phases[Number(tuple[2])] as DiagnosticPhase,
      source: dictionaries.sources[Number(tuple[3])]!,
      level: dictionaries.levels[Number(tuple[4])] as DiagnosticLevel,
      code: dictionaries.codes[Number(tuple[5])]!,
      ...(tuple[6] === null ? {} : { commandSequence: Number(tuple[6]) }),
      ...(entityIds === undefined ? {} : { entityIds }),
      ...(cardIndices === undefined ? {} : { cardIds: cardIndices.map((index) => dictionaries.cards[index] as CardId) }),
      ...(tuple[9] === null ? {} : { correlationId: dictionaries.correlations[Number(tuple[9])]! }),
      payload: decode(tuple[10]!),
    };
  });
  return {
    manifest: compact.manifest,
    replay: compact.replay,
    records,
    checkpoints: compact.checkpoints,
    metrics: compact.metrics,
    findings: compact.findings,
    summary: compact.summary,
  };
}

export function normalizeDiagnosticBundle(value: unknown): MatchDiagnosticBundle {
  return isCompactDiagnosticBundle(value) ? expandDiagnosticBundle(value) : value as MatchDiagnosticBundle;
}
